import { lazy, Suspense } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { QueryClient, QueryClientProvider, MutationCache, QueryCache } from '@tanstack/react-query';
import { Toaster, toast } from 'sonner';
import { ApiError, toastFromApiError, type ToastFn } from '@shared/api-errors';
import { AuthProvider } from './contexts/AuthContext';
import { BrandingProvider } from './contexts/BrandingContext';
import { useAuth } from './hooks/useAuth';
import { ErrorBoundary } from './components/ErrorBoundary';
import PortalLayout from './layouts/PortalLayout';
import Login from './pages/Login';

// Lazy-loaded pages
const Dashboard = lazy(() => import('./pages/Dashboard'));
const Tickets = lazy(() => import('./pages/Tickets'));
const TicketDetail = lazy(() => import('./pages/TicketDetail'));
const NewTicket = lazy(() => import('./pages/NewTicket'));
const KnowledgeBase = lazy(() => import('./pages/KnowledgeBase'));
const KBArticle = lazy(() => import('./pages/KBArticle'));
const Announcements = lazy(() => import('./pages/Announcements'));
const Contracts = lazy(() => import('./pages/Contracts'));
const Licenses = lazy(() => import('./pages/Licenses'));
const Profile = lazy(() => import('./pages/Profile'));

const PageFallback = () => (
  <div className="flex h-64 items-center justify-center">
    <div className="w-8 h-8 border-4 border-blue-600 border-t-transparent rounded-full animate-spin" />
  </div>
);

// sonner's `toast.error(title, { description })` signature needs bridging to
// api-errors' ToastFn. Surface the resolved TR message as the visible line.
const toastAdapter: ToastFn = (p) => {
  toast.error(p.title, { description: p.description });
};

// queryClient is module-scoped. <Toaster /> must be rendered in the component
// tree (mounted below in App) so cache onError toasts surface.
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: (failureCount, err) => {
        if (err instanceof ApiError && err.status >= 400 && err.status < 500) return false;
        return failureCount < 1;
      },
      staleTime: 5 * 60 * 1000,
    },
    mutations: {
      retry: (failureCount, err) => {
        if (err instanceof ApiError && err.status >= 400 && err.status < 500) return false;
        return failureCount < 1;
      },
    },
  },
  mutationCache: new MutationCache({
    onError: (err, _vars, _ctx, mutation) => {
      if (mutation.options.onError) return;
      if (mutation.options.meta?.suppressErrorToast) return;
      toastFromApiError(err, toastAdapter);
    },
  }),
  queryCache: new QueryCache({
    onError: (err, query) => {
      if (query.meta?.suppressErrorToast) return;
      toastFromApiError(err, toastAdapter);
    },
  }),
});

// Protected Route Component
function ProtectedRoute({ children }: { children: React.ReactNode }) {
  const { isAuthenticated, isLoading } = useAuth();

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="w-8 h-8 border-4 border-blue-600 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  return <>{children}</>;
}

// Public Route Component (redirects to home if authenticated)
function PublicRoute({ children }: { children: React.ReactNode }) {
  const { isAuthenticated, isLoading } = useAuth();

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="w-8 h-8 border-4 border-blue-600 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (isAuthenticated) {
    return <Navigate to="/" replace />;
  }

  return <>{children}</>;
}

function AppRoutes() {
  return (
    <Routes>
      {/* Public Routes */}
      <Route
        path="/login"
        element={
          <PublicRoute>
            <Login />
          </PublicRoute>
        }
      />

      {/* Protected Routes */}
      <Route
        path="/"
        element={
          <ProtectedRoute>
            <PortalLayout />
          </ProtectedRoute>
        }
      >
        <Route index element={<Suspense fallback={<PageFallback />}><Dashboard /></Suspense>} />
        <Route path="tickets" element={<Suspense fallback={<PageFallback />}><Tickets /></Suspense>} />
        <Route path="tickets/new" element={<Suspense fallback={<PageFallback />}><NewTicket /></Suspense>} />
        <Route path="tickets/:id" element={<Suspense fallback={<PageFallback />}><TicketDetail /></Suspense>} />
        <Route path="contracts" element={<Suspense fallback={<PageFallback />}><Contracts /></Suspense>} />
        <Route path="licenses" element={<Suspense fallback={<PageFallback />}><Licenses /></Suspense>} />
        <Route path="kb" element={<Suspense fallback={<PageFallback />}><KnowledgeBase /></Suspense>} />
        <Route path="kb/:slug" element={<Suspense fallback={<PageFallback />}><KBArticle /></Suspense>} />
        <Route path="announcements" element={<Suspense fallback={<PageFallback />}><Announcements /></Suspense>} />
        <Route path="announcements/:id" element={<Suspense fallback={<PageFallback />}><Announcements /></Suspense>} />
        <Route path="profile" element={<Suspense fallback={<PageFallback />}><Profile /></Suspense>} />
      </Route>

      {/* 404 */}
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

function App() {
  return (
    <ErrorBoundary>
      <QueryClientProvider client={queryClient}>
        <BrowserRouter>
          <BrandingProvider>
            <AuthProvider>
              <AppRoutes />
              <Toaster richColors position="top-right" />
            </AuthProvider>
          </BrandingProvider>
        </BrowserRouter>
      </QueryClientProvider>
    </ErrorBoundary>
  );
}

export default App;
